// src/hooks/useBCPFormState.ts - Form state management for BCP
import { useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { getUserBCP, AnalysisStruct, BCPSuggestions } from '../services/api';
import { FormState, ValidationErrors, RiskState, BCPFormState } from '../types/bcp';
import { logger } from '../utils/logger';

// BCPFormState interface is now imported from '../types/bcp'

export const useBCPFormState = (): BCPFormState => {
  const { id: urlId } = useParams<{ id: string }>();

  // Core form state
  const [formData, setFormData] = useState<FormState>({
    business_name: '',
    business_type: '',
    industry: '',
    employee_count: 0,
    annual_revenue: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'USA',
    primary_location: {
      name: '', contact: '', address: '', city: '', state: '', zip: '', phone: ''
    },
    additional_locations: [],
    critical_functions: [],
    key_suppliers: [],
    it_systems: [],
    mitigations: [],
    max_downtime: '',
    recovery_priority: '',
    rpo: '',
    risk_impact_assessments: {
      primary_concerns: {},
      location_risks: {}
    }
  });

  const [errors, setErrors] = useState<ValidationErrors>({});
  const [touched, setTouched] = useState<{ [key: string]: boolean }>({});

  // BCP metadata
  const [bcpId, setBcpId] = useState<string | undefined>(urlId);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // BCP generation state
  const [bcpGenerated, setBcpGenerated] = useState(false);
  const [generationStep, setGenerationStep] = useState<'saving' | 'generating' | 'complete'>('saving');
  const [generatedBCPId, setGeneratedBCPId] = useState<number | null>(null);

  // Risk and analysis state
  const [risks, setRisks] = useState<RiskState>({ primary_concerns: [], location_risks: [] });
  const [analysis, setAnalysis] = useState<AnalysisStruct | null>(null);
  const [suggestions, setSuggestions] = useState<BCPSuggestions | null>(null);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [suggestionsApplied, setSuggestionsApplied] = useState(false);

  // Original data for change detection
  const [originalBCPData, setOriginalBCPData] = useState<any>(null);

  // Team management
  const [employees, setEmployees] = useState<any[]>([]);

  // Load BCP data function
  const loadBCPData = useCallback(async () => {
    if (!bcpId) return; // Not editing, creating new BCP

    try {
      setIsLoading(true);
      setIsEditing(true);
      const bcpData = await getUserBCP(parseInt(bcpId));

      logger.debug('Raw BCP data loaded', bcpData, 'BCP_FORM');
      logger.debug('Business fields loaded', {
        business_type: bcpData.business_type,
        employee_count: bcpData.employee_count,
        annual_revenue: bcpData.annual_revenue
      }, 'BCP_FORM');

      // Store original BCP data for change detection
      setOriginalBCPData(bcpData);

      // Transform BCP data to form data format
      setFormData({
        business_name: bcpData.business_name || '',
        business_type: bcpData.business_type || '',
        industry: bcpData.industry || '',
        employee_count: Number(bcpData.employee_count) || 0,
        annual_revenue: bcpData.annual_revenue || '',
        address: bcpData.address || '',
        city: bcpData.city || '',
        state: bcpData.state || '',
        zip_code: bcpData.zip_code || '',
        country: bcpData.country || 'USA',
        primary_location: {
          name: bcpData.primary_location?.name || '',
          contact: bcpData.primary_location?.contact || '',
          address: bcpData.primary_location?.address || bcpData.address || '',
          city: bcpData.primary_location?.city || bcpData.city || '',
          state: bcpData.primary_location?.state || bcpData.state || '',
          zip: bcpData.primary_location?.zip || bcpData.zip_code || '',
          phone: bcpData.primary_location?.phone || ''
        },
        additional_locations: bcpData.additional_locations || [],
        critical_functions: bcpData.critical_functions || [],
        key_suppliers: bcpData.key_suppliers || [],
        it_systems: bcpData.it_systems || [],
        mitigations: bcpData.mitigations || [],
        // Business Impact Analysis fields
        financial_impact_severity: bcpData.financial_impact_severity,
        operational_impact_severity: bcpData.operational_impact_severity,
        reputational_impact_severity: bcpData.reputational_impact_severity,
        legal_regulatory_impact_severity: bcpData.legal_regulatory_impact_severity,
        financial_impact_description: bcpData.financial_impact_description,
        operational_impact_description: bcpData.operational_impact_description,
        reputational_impact_description: bcpData.reputational_impact_description,
        legal_regulatory_impact_description: bcpData.legal_regulatory_impact_description,
        max_downtime: bcpData.max_downtime,
        recovery_priority: bcpData.recovery_priority,
        rpo: bcpData.rpo || '',
        risk_impact_assessments: bcpData.risk_impact_assessments || {
          primary_concerns: {},
          location_risks: {}
        },
      });

      // Load risks if they exist
      logger.debug('Loading risks from database', {
        risk_factors: bcpData.risk_factors,
        threat_probability: bcpData.threat_probability
      }, 'BCP_FORM');

      if (bcpData.risk_factors) {
        const loadedRisks = {
          primary_concerns: bcpData.risk_factors.selected || [],
          location_risks: bcpData.threat_probability?.selected || []
        };

        console.log('🔍 [BCP FORM] Loading risks from backend:', {
          raw_risk_factors: bcpData.risk_factors,
          raw_threat_probability: bcpData.threat_probability,
          loaded_risks: loadedRisks
        });

        // Also log BIA fields to see what's loaded
        console.log('🔍 [BCP FORM] Loading BIA fields from backend:', {
          financial_impact_severity: bcpData.financial_impact_severity,
          operational_impact_severity: bcpData.operational_impact_severity,
          reputational_impact_severity: bcpData.reputational_impact_severity,
          legal_regulatory_impact_severity: bcpData.legal_regulatory_impact_severity,
          max_downtime: bcpData.max_downtime,
          recovery_priority: bcpData.recovery_priority,
          risk_impact_assessments: bcpData.risk_impact_assessments
        });

        // Log the risk impact assessments in detail
        if (bcpData.risk_impact_assessments) {
          console.log('🔍 [BCP FORM] Risk Impact Assessments loaded:', {
            primary_concerns: bcpData.risk_impact_assessments.primary_concerns,
            location_risks: bcpData.risk_impact_assessments.location_risks,
            location_risks_keys: Object.keys(bcpData.risk_impact_assessments.location_risks || {}),
            primary_concerns_keys: Object.keys(bcpData.risk_impact_assessments.primary_concerns || {})
          });
        }

        logger.debug('Setting risks state', loadedRisks, 'BCP_FORM');
        setRisks(loadedRisks);
      } else {
        logger.debug('No risk factors found in database', undefined, 'BCP_FORM');
      }

      // Load stored AI analysis if available
      if (bcpData.ai_analysis) {
        setAnalysis(bcpData.ai_analysis);
        setBcpGenerated(true);
      }

    } catch (error) {
      logger.error('Error loading BCP data', error, 'BCP_FORM');
      // If loading fails, stay in create mode
      setIsEditing(false);
    } finally {
      setIsLoading(false);
    }
  }, [bcpId]);

  return {
    // Core form state
    formData,
    setFormData,
    errors,
    setErrors,
    touched,
    setTouched,

    // BCP metadata
    bcpId,
    setBcpId,
    isEditing,
    setIsEditing,
    isLoading,
    setIsLoading,

    // Submission state
    submitting,
    setSubmitting,
    submitSuccess,
    setSubmitSuccess,
    submitError,
    setSubmitError,

    // BCP generation state
    bcpGenerated,
    setBcpGenerated,
    generationStep,
    setGenerationStep,
    generatedBCPId,
    setGeneratedBCPId,

    // Risk and analysis state
    risks,
    setRisks,
    analysis,
    setAnalysis,
    suggestions,
    setSuggestions,
    isLoadingSuggestions,
    setIsLoadingSuggestions,
    suggestionsApplied,
    setSuggestionsApplied,

    // Original data for change detection
    originalBCPData,
    setOriginalBCPData,

    // Team management
    employees,
    setEmployees,

    // Load BCP data function
    loadBCPData,
  };
};
